import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import ConnectedClasses from "@/components/settings/ConnectedClasses";
import { Card, SectionHeader, Button } from "@/components/studio/primitives";

/** Organization enrollment stays in Hans; only class logistics cross the bridge. */
export default function OrganizationClassScheduling({ cls, onChanged }) {
  const [connection, setConnection] = useState(null), [loading, setLoading] = useState(true);
  const [classSetup, setClassSetup] = useState(null);
  const [roster, setRoster] = useState(null), [keep, setKeep] = useState([]), [names, setNames] = useState("");
  const [meetings, setMeetings] = useState([]), [error, setError] = useState(""), [saving, setSaving] = useState(false);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const base = `/org/scheduler/connections/${encodeURIComponent(cls.scheduler_connection_id || "")}/classes/${encodeURIComponent(cls.class_id)}`;
  const load = useCallback(async (signal) => {
    setLoading(true); setError("");
    try {
      if (!cls.scheduler_offering_id) {
        const { data } = await orgApi.get("/org/scheduler/connection", { signal });
        if (!signal?.aborted) { setConnection(data.connection || null); setClassSetup(data.class_setup || null); }
      } else {
        const [schedule, members] = await Promise.all([
          orgApi.get(`${base}/meetings`, { signal }),
          cls.enrollment_mode === "org_managed" ? orgApi.get(`${base}/roster`, { signal }) : Promise.resolve(null),
        ]);
        if (!signal?.aborted) {
          setMeetings(schedule.data.sessions || []);
          if (members) { setRoster(members.data); setKeep(members.data.member_ids); }
        }
      }
    } catch (err) { if (!signal?.aborted) setError(getOrgErrorMessage(err, "Could not load this class's schedule.")); }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [base, cls.scheduler_offering_id, cls.enrollment_mode]);
  useEffect(() => { const controller = new AbortController(); load(controller.signal); return () => controller.abort(); }, [load]);
  const newNames = names.split(/\r?\n/).map((name) => name.trim()).filter(Boolean);
  async function save() {
    setSaving(true); setError("");
    try {
      const { data } = await orgApi.put(`${base}/roster`, { request_id: requestId,
        expected_member_ids: roster.member_ids, keep_member_ids: keep, new_names: newNames });
      setRoster(data); setKeep(data.member_ids); setNames(""); setRequestId(crypto.randomUUID()); onChanged?.();
    } catch (err) { setError(getOrgErrorMessage(err, "Could not save the roster. Refresh to review the current students.")); }
    finally { setSaving(false); }
  }
  if (loading) return <Card><p role="status">Loading class scheduling…</p></Card>;
  return <div className="mb-6 space-y-6 text-[var(--text-primary)]">
    {error && <Card><p role="alert" className="text-[var(--state-danger)]">{error}</p><Button variant="ghost" onPress={() => load()}>Refresh class</Button></Card>}
    {!cls.scheduler_offering_id && (classSetup?.ready
      ? <Card><ConnectedClasses connection={connection} organization initialClass={cls} onCreated={onChanged} /></Card>
      : !error && classSetup && <Card><p>{classSetup.message}</p>
        {/* Only offer the dashboard when the fix is actually there; it used to
            link to a page whose setup panel the same condition had hidden. */}
        {classSetup.blocked_on === "connection" && <Link to="/org/settings/schedule" className="mt-3 inline-block text-[var(--accent-yellow-text)] underline">Open organization scheduling</Link>}
      </Card>)}
    {roster && <Card>
      <SectionHeader>Student roster</SectionHeader>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">Names stay in Hans. Students need no email or account. Uncheck a student to withdraw them from the class; their previous attendance and lesson history stay intact.</p>
      <fieldset disabled={saving} className="space-y-3">
        {roster.students.map((student) => <label key={student.id} className="flex items-center gap-3">
          <input type="checkbox" className="accent-[var(--accent-yellow)]" checked={keep.includes(student.id)} onChange={(e) => { setKeep((ids) => e.target.checked ? [...ids, student.id] : ids.filter((id) => id !== student.id)); setRequestId(crypto.randomUUID()); }} />
          {student.name}
        </label>)}
        <label className="block text-sm">Add student names — one per line<textarea rows={4} value={names}
          className="mt-2 block w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] p-3 focus:border-[var(--accent-yellow-border-strong)] focus:outline-none"
          onChange={(e) => { setNames(e.target.value); setRequestId(crypto.randomUUID()); }} /></label>
        <p className="text-sm text-[var(--text-secondary)]">{keep.length + newNames.length} of {roster.capacity} seats</p>
        <Button disabled={saving || keep.length + newNames.length > roster.capacity} onPress={save}>Save roster</Button>
      </fieldset>
    </Card>}
    {cls.scheduler_offering_id && !error && <Card>
      <SectionHeader>Class meetings</SectionHeader>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">Meeting dates come from Kalver Scheduler. Instructors open each meeting in Hans Today for attendance and lesson notes.</p>
      <ul className="divide-y divide-[var(--studio-divider)]">{meetings.map((meeting) => <li key={meeting.id} className="py-3 text-sm">
        <span>{new Date(meeting.start).toLocaleString()} – {new Date(meeting.end).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
        {meeting.status === "canceled" && <span className="ml-3 text-[var(--text-secondary)]">Canceled</span>}
      </li>)}</ul>
      <Link to="/org/settings/schedule" className="mt-4 inline-block text-sm text-[var(--accent-yellow-text)] underline">Open Scheduler from Settings → Schedule to change meeting dates</Link>
    </Card>}
  </div>;
}
